import React from 'react';
import { CheckCircle, XCircle, MapPin, Radio, Box, Building2 } from 'lucide-react';

const CoverageResults = ({ result, onConfirmBuilding }) => {
    if (!result) return null;

    const { viable, reason, details, buildingsFound } = result;
    const isSuccess = viable;

    // Check if we have multiple buildings to resolve
    const hasMultipleBuildings = buildingsFound && buildingsFound.length > 1 && (!details || !details.building || details.building === "Múltiplos edifícios encontrados");

    return (
        <div className={`rounded-xl shadow-lg border p-6 transition-all duration-300 ${isSuccess ? 'bg-green-50 dark:bg-green-900/20 border-green-200 dark:border-green-800' : 'bg-red-50 dark:bg-red-900/20 border-red-200 dark:border-red-800'}`}>
            <div className="flex items-start space-x-4">
                <div className={`p-3 rounded-full ${isSuccess ? 'bg-green-100 text-green-600 dark:bg-green-800 dark:text-green-200' : 'bg-red-100 text-red-600 dark:bg-red-800 dark:text-red-200'}`}>
                    {isSuccess ? <CheckCircle className="w-8 h-8" /> : <XCircle className="w-8 h-8" />}
                </div>
                <div className="flex-1">
                    <h3 className={`text-lg font-bold mb-1 ${isSuccess ? 'text-green-800 dark:text-green-100' : 'text-red-800 dark:text-red-100'}`}>
                        {isSuccess ? 'Cobertura Disponível' : 'Cobertura Indisponível'}
                    </h3>
                    <p className={`text-sm mb-4 ${isSuccess ? 'text-green-700 dark:text-green-200' : 'text-red-700 dark:text-red-200'}`}>
                        {reason}
                    </p>

                    {hasMultipleBuildings && (
                        <div className="mt-4 p-4 bg-white dark:bg-slate-800 rounded-lg border border-yellow-300 dark:border-yellow-700">
                            <h4 className="font-semibold text-yellow-800 dark:text-yellow-400 mb-2 flex items-center">
                                <Building2 className="w-5 h-5 mr-2" />
                                Múltiplos Edifícios/Condomínios Encontrados
                            </h4>
                            <p className="text-sm text-slate-600 dark:text-slate-400 mb-3">
                                Identificamos mais de um local correspondente. Por favor, selecione o correto para confirmar a viabilidade:
                            </p>
                            <div className="space-y-2 max-h-48 overflow-y-auto">
                                {buildingsFound.slice(0, 20).map((building, i) => (
                                    <button
                                        key={building.id || i}
                                        onClick={() => onConfirmBuilding && onConfirmBuilding(building)}
                                        className="w-full text-left p-3 text-sm bg-slate-50 hover:bg-blue-50 dark:bg-slate-700 dark:hover:bg-slate-600 border border-slate-200 dark:border-slate-600 rounded-md transition-colors flex justify-between items-center"
                                    >
                                        <span className="font-medium text-slate-800 dark:text-slate-200">{building.name}</span>
                                        {building.distance !== undefined && (
                                            <span className="text-xs text-slate-500 dark:text-slate-400">{Math.round(building.distance)}m</span>
                                        )}
                                    </button>
                                ))}
                            </div>
                            {buildingsFound.length > 20 && (
                                <div className="mt-2 text-xs text-center text-slate-500 dark:text-slate-400">
                                    Exibindo os primeiros 20 de {buildingsFound.length} locais encontrados.
                                </div>
                            )}
                        </div>
                    )}

                    {isSuccess && !hasMultipleBuildings && (
                        <div className="space-y-3 mt-4 border-t border-green-200 dark:border-green-700/50 pt-4">
                            {details?.building && (
                                <div className="flex items-center text-sm text-slate-700 dark:text-slate-300 font-medium bg-blue-50 dark:bg-blue-900/20 p-2 rounded-md border border-blue-200 dark:border-blue-800">
                                    <Building2 className="w-4 h-4 mr-2 text-blue-600 dark:text-blue-400" />
                                    <span>Condomínio / Edifício:</span> <span className="ml-1 text-blue-800 dark:text-blue-300">{details.building}</span>
                                </div>
                            )}
                            {details?.sector && (
                                <div className="flex items-center text-sm text-slate-700 dark:text-slate-300">
                                    <Radio className="w-4 h-4 mr-2 text-blue-500" />
                                    <span className="font-medium mr-1">Setor:</span> {details.sector}
                                </div>
                            )}
                            {details?.olt && (
                                <div className="flex items-center text-sm text-slate-700 dark:text-slate-300">
                                    <Box className="w-4 h-4 mr-2 text-purple-500" />
                                    <span className="font-medium mr-1">OLT:</span> {details.olt}
                                </div>
                            )}
                            {details?.nearestCto && (
                                <div className="bg-white dark:bg-slate-800 p-3 rounded-lg border border-slate-200 dark:border-slate-700 mt-2">
                                    <div className="flex items-center justify-between mb-1">
                                        <span className="text-xs font-semibold uppercase text-slate-500 dark:text-slate-400">
                                            {details.nearestCto.isOutsideBuilding ? "CTO mais próxima fora do prédio" : "CTO Mais Próxima"}
                                        </span>
                                        <span className="bg-blue-100 text-blue-800 text-xs font-bold px-2 py-0.5 rounded dark:bg-blue-900 dark:text-blue-200">
                                            {details.nearestCto.distance}
                                        </span>
                                    </div>
                                    <div className="flex items-center text-slate-800 dark:text-slate-200 font-medium">
                                        <MapPin className="w-4 h-4 mr-1.5 text-red-500" />
                                        {details.nearestCto.name}
                                    </div>
                                </div>
                            )}
                        </div>
                    )}

                    {!isSuccess && details.nearestCto && (
                        <div className="mt-4 pt-4 border-t border-red-200 dark:border-red-800/50">
                            <p className="text-xs font-semibold text-slate-500 dark:text-slate-400 mb-2">PONTO MAIS PRÓXIMO ENCONTRADO:</p>
                             <div className="flex items-center justify-between bg-white/50 dark:bg-black/20 p-2 rounded">
                                <span className="text-sm font-medium text-slate-700 dark:text-slate-300">{details.nearestCto.name}</span>
                                <span className="text-xs font-bold text-red-600 dark:text-red-400">{details.nearestCto.distance}</span>
                             </div>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};

export default CoverageResults;
